<template>
  <div class="activity-stage scrolled-secrets">
    <div class="left-side">
      <div class="instructions-scrolled-secrets">
        Scroll down to read the passage. Move each label to the correct place. Then type the word.
      </div>
      <div class="scrollable-container">
        <h3 class="round-title">
          {{ currentRound.roundTitle }}
        </h3>
        <img :src="currentRound.image" alt="" />
        <div class="description" v-for="(paragraph, idx) in currentRound.paragraphs" :key="idx">
          <div v-html="paragraph"></div>
        </div>
      </div>
    </div>
    <div class="right-side">
      <div class="sentences-list">
        <div class="sentence-ct" v-for="(option, idx) in currentRound.options" :key="idx">
          <div
            v-if="!options[idx].replaced"
            class="sentence-item droppables"
            :id="`droppable-${option.id}`"
          >
            <svg height="50" width="160" class="border-style" v-html="cardBorder"></svg>
            <p class="type-line">{{ option.text }}</p>
          </div>
        </div>
      </div>
      <div class="results-container" v-if="currentRound.options">
        <div
          class="results-row column-row"
          v-for="(option, idx) in currentRound.options"
          :key="idx"
        >
          <div class="sentence-definition">
            <span v-text="option.label"></span>
          </div>
          <div class="sentence-definition"></div>
          <div class="sentence-placeholder target-slot">
            <svg
              height="50"
              width="160"
              class="border-style"
              v-html="cardBorderGrey"
              v-if="!options[idx].replaced"
              :id="`droparea-${idx}`"
            ></svg>

            <svg
              height="50"
              width="160"
              class="border-style dd"
              v-html="cardBorder"
              v-if="options[idx].replaced"
            ></svg>

            <blinking-cursor-text
              :class-name="'type-line'"
              :text="options[idx].text"
              :text-step="options[idx].stepNumber"
              :round-id="currentRound.roundIndex"
              v-if="options[idx].replaced"
            >
            </blinking-cursor-text>
          </div>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import Draggable from 'gsap/Draggable';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import cardBorder from 'assets/svg/scrolled-secrets/scroll-secrets-box.svg';
import cardBorderGrey from 'assets/svg/scrolled-secrets/scroll-secrets-box-grey.svg';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText
  },

  data() {
    return {
      cardBorder,
      cardBorderGrey,
      draggables: [],
      options: [],
      steps: []
    };
  },

  methods: {
    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.scrolled-secrets');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 20}px)`;
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages(activityState.rounds.map(({ image }) => image));
    },

    async loadImages(images) {
      this.queueImage(images);
      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          delay: 0,
          voice: `${this.voiceInstructionsPrefix}/scrolledsecrets-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    handleDrop(context) {
      const {
        target,
        vars: { id }
      } = context;
      const dropArea = document.getElementById(`droparea-${id}`);

      if (context.hitTest(dropArea, '25%')) {
        this.sndBell();

        const contentPanel = document.querySelector('.activity-stage');
        const contentPanelPosition = contentPanel.getBoundingClientRect();

        // get DOM rects for target and destination
        const targetRect = target.getBoundingClientRect();
        const dropAreaRect = dropArea.getBoundingClientRect();

        const tweenStartPos = {
          left: `${targetRect.left - contentPanelPosition.left}px`,
          top: `${targetRect.top - contentPanelPosition.top}px`,
          position: 'absolute'
        };

        const tweenEndPos = {
          left: `${dropAreaRect.left - contentPanelPosition.left - 2}px`,
          top: `${dropAreaRect.top - contentPanelPosition.top - 2}px`,
          position: 'absolute'
        };

        context.kill();
        TweenMax.set(target, { clearProps: 'all' });
        TweenMax.fromTo(
          target,
          0.65,
          { css: tweenStartPos },
          {
            css: tweenEndPos,
            onComplete: () => this.onDragTransitionComplete(id, dropArea, target)
          }
        );

        this.disableDragging();
      } else {
        this.sndError();
        TweenMax.to(target, 0.5, { css: { x: 0, y: 0 } });
      }
    },

    onDragTransitionComplete(id) {
      const { text } = this.options[id];

      this.options[id].replaced = true;
      this.options[id].stepNumber = this.steps.length;
      this.steps = [...this.steps, { answer: text }];

      this.overwriteRoundSteps({
        steps: this.steps,
        roundId: this.currentRound.roundIndex,
        stepIndex: this.options[id].stepNumber
      });

      this.overwriteProgressText({ currentText: text });
      this.draggables = this.draggables.filter(item => item.id !== id);
    },

    disableDragging() {
      this.draggables.forEach(item => item.draggable[0].disable());
    },

    enableDragging() {
      this.draggables.forEach(item => item.draggable[0].enable());
    },

    createDraggables() {
      let bounds = null;
      const self = this;
      const { isMobile } = this.userDevice;
      const { options } = this.currentRound;
      const draggables = [];

      options.forEach((option, idx) => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        const draggable = Draggable.create(`#droppable-${idx}`, {
          id: idx,
          cursor: 'grab',
          text: option.text,
          edgeResistance: isMobile ? 1.1 : 0.65,
          onPress() {
            if (bounds) return;
            // get DOMRect bounding
            bounds = getActivityBoundings();
          },
          onDragStart() {
            TweenMax.set(`#droppable-${idx}`, {
              cursor: 'grabbing',
              css: { 'z-index': 110 }
            });
          },
          onDrag() {
            if (!isMobile) return;
            // work-around for iOS not firing touchend on tablet
            clearTimeout(dragTimer);
            const hasTouched = hasTouchedBoundings(this.pointerX, this.pointerY, bounds);
            if (hasTouched && !hasHitBoundary) {
              dragTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDrop(this);
              }, 200);
            }
          },
          onDragEnd() {
            clearTimeout(dragTimer);
            hasHitBoundary = false;
            self.handleDrop(this);
          }
        });

        draggables.push({ draggable, id: idx });
      });

      this.draggables = draggables;
    },

    fadeInContainer() {
      const self = this;

      TweenMax.to('.scrolled-secrets', 1, {
        opacity: 1,
        onComplete() {
          self.createDraggables();
        }
      });
    },

    setOptions() {
      this.options = this.currentRound.options.map(option => ({
        ...option,
        replaced: false,
        stepNumber: 0
      }));
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.setOptions();
      this.$nextTick(() => {
        this.setContainerHeight();
        this.$nextTick(this.fadeInContainer);
      });
    },

    handleCorrectKey() {
      const { draggables } = this;

      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();
        this.enableDragging();

        if (!draggables.length) {
          TweenMax.delayedCall(2, this.completeActivity);
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.target-slot .type-line {
  text-indent: 0;
  margin-left: 10px;
}

.scrolled-secrets {
  height: 460px;
  padding: 10px;
  box-sizing: border-box;
}

.left-side {
  float: left;
  display: flex;
  flex-flow: column;
  max-width: 48%;
  height: 100%;

  .instructions-scrolled-secrets {
    font-size: 14px;
    font-family: 'hwtfutura-semibold';
  }

  .scrollable-container {
    .description {
      color: #000;
      font-size: 16px;
      font-family: 'hwtfutura';
    }
  }

  .round-title {
    text-align: center;
    font-weight: bold;
    font-size: 20px;
  }

  .scrollable-container {
    border: 2px solid black;
    padding: 10px;
    width: 100%;
    flex: 1;
    overflow-y: scroll;
    img {
      width: 100%;
    }
  }
}

.right-side {
  display: flex;
  flex-flow: column;
  vertical-align: top;
  padding-left: 20px;
  max-width: 48%;
  height: 100%;

  .sentences-list {
    .sentence-ct {
      width: 160px;
      height: 50px;
      display: inline-block;
      vertical-align: top;
      margin: 5px;

      .sentence-item {
        width: 200px;
        height: 50px;
        line-height: 50px;
        z-index: 150;

        .border-style {
          z-index: -1;
          position: absolute;
          left: 0;
        }
      }

      .droppables {
        z-index: 100;
        position: relative;
        p {
          font-size: 20px;
        }
      }
    }
  }

  .results-container {
    border: 2px solid black;
    background-color: #cccccc;
    width: 100%;
    flex: 1;
    overflow-y: auto;
    margin-left: 10px;

    .results-row {
      display: flex;
      flex-direction: row;
    }

    .column-title {
      font-family: 'hwtfutura-semibold';
      font-size: 18px;
      line-height: 40px;
      .sentence-placeholder {
        height: 10px;
        line-height: 10px;
      }
    }
    .column-row {
      font-family: 'hwtfutura-semibold';
      font-size: 16px;
      line-height: 50px;
    }
    .sentence-definition {
      display: inline-block;
      flex-grow: 1;
      text-align: center;
      vertical-align: top;
      color: black;
    }
    .sentence-definition-right {
      display: inline-block;
      flex-grow: 1;
      text-align: center;
      vertical-align: top;
      color: black;
    }
    .sentence-placeholder {
      display: inline-block;
      width: 200px;
      height: 50px;
      line-height: 50px;
      margin: 2px;
      position: relative;
    }
  }

  .droppables,
  .noselect,
  svg {
    cursor: pointer;
  }
}

.type-line {
  position: absolute;
  top: 0;
  left: 0;
  text-indent: 10px;
  line-height: 50px;
  font-size: 20px;
}

.sentence-item {
  transform: translate3d(0px, 0px, 0px);
  cursor: move;
  touch-action: none;
  user-select: none;
}

.left-side,
.right-side {
  box-sizing: border-box;
}

.scrolled-secrets {
  opacity: 0;
}

.sentence-item {
  width: 160px;
}
</style>
